import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";
import logo from "@/assets/logo.webp";

// One menu on every page, the homepage included: real pages rather than
// in-page anchors, so the links match the crawler header in static-prerender.
const NAV_LINKS = [
  { href: "/get-the-pill-online/", label: "Get the Pill Online" },
  { href: "/how-it-works/", label: "How It Works" },
  { href: "/guides/", label: "Guides" },
  { href: "/reviews/", label: "Reviews" },
  { href: "/faqs/", label: "FAQs" },
  { href: "/about-us/", label: "About Us" },
  { href: "/contact-us/", label: "Contact us" },
];
const trimSlash = (p: string) => (p.length > 1 ? p.replace(/\/+$/, "") : p);

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const location = useLocation();

  useEffect(() => {
    let lastY = window.scrollY;
    const handleScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 50);
      if (y < 100) {
        setIsVisible(true);
      } else if (y < lastY) {
        // scrolling up, reveal
        setIsVisible(true);
      } else if (y > lastY) {
        // scrolling down past threshold, hide
        setIsVisible(false);
      }
      lastY = y;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (href: string) => trimSlash(location.pathname) === trimSlash(href);

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 px-4 pt-3 transition-all duration-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'}`}>
        <div
          className={`max-w-lg lg:max-w-6xl xl:max-w-7xl mx-auto rounded-2xl transition-all duration-300 ${
            isScrolled 
              ? "bg-card/70 backdrop-blur-2xl backdrop-saturate-150 shadow-md border border-border/40" 
              : "bg-card/60 backdrop-blur-xl backdrop-saturate-150 shadow-sm border border-border/30"
          }`}

        >
          <div className="px-4 lg:px-6 py-2.5 flex items-center justify-between gap-3">
            <a href="/" className="flex flex-col items-start min-w-0">
              <div className="flex items-center gap-2">
                <img src={logo} alt="Contraception Direct" className="h-7 w-auto flex-shrink-0" width={187} height={49} />
              </div>
              <span className="text-[9px] text-muted-foreground/50 font-semibold uppercase tracking-[0.12em] whitespace-nowrap mt-0.5 ml-2 flex items-center gap-0.5">
                by Pharmacy Services Direct <span className="text-[9px] leading-none -mt-[2px] -ml-0.5" style={{ fontWeight: 950 }}>+</span>
              </span>
            </a>

            <div className="hidden lg:flex items-center gap-6">
              {NAV_LINKS.map((link) => (
                <a key={link.href} href={link.href} className={`text-sm transition-colors font-medium ${isActive(link.href) ? 'text-primary' : 'text-foreground/70 hover:text-primary'}`}>
                  {link.label}
                </a>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <a href="/book/" onClick={() => trackEvent("cta_click", { location: location.pathname, cta: "navbar" })}>
                <Button variant="cta" size="sm" className="hidden sm:flex rounded-full px-4 gap-1.5 text-sm h-8">
                  Start now
                </Button>
              </a>
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden w-8 h-8 rounded-full bg-muted/40 flex items-center justify-center transition-colors hover:bg-muted">
                {isMobileMenuOpen ? <X className="w-4 h-4 text-foreground" /> : <Menu className="w-4 h-4 text-foreground" />}
              </button>
            </div>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden mt-2 mx-auto max-w-lg bg-card/95 backdrop-blur-xl rounded-2xl border border-border/40 shadow-lg overflow-hidden">
            <div className="p-3 space-y-1">
              {NAV_LINKS.map((link) => (
                <a key={link.href} href={link.href} onClick={() => setIsMobileMenuOpen(false)} className={`block py-2.5 px-4 hover:bg-muted/50 rounded-xl transition-all text-sm ${isActive(link.href) ? 'text-primary bg-muted/30' : 'text-foreground hover:text-primary'}`}>
                  {link.label}
                </a>
              ))}
              <div className="pt-2 border-t border-border/30 mt-2">
                <a href="/book/" onClick={() => { trackEvent("cta_click", { location: location.pathname, cta: "navbar-mobile" }); setIsMobileMenuOpen(false); }}>
                  <Button variant="cta" size="sm" className="w-full justify-center rounded-full gap-2">Start now</Button>
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
